import React from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  FileCopy as FileCopyIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import useConfirmDialog from "../../hooks/useConfirmDialog";

/**
 * Generic DataGrid Actions component with flexible action configuration
 *
 * @param {Object} props
 * @param {Object} props.item - The data item/row from the DataGrid
 * @param {Array} props.actions - Array of action configurations
 * @param {Function} props.onAction - Optional global action handler
 * @param {Object} props.events - Optional events object for tracking
 * @param {string} props.menuId - Optional custom menu ID for accessibility
 *
 * Action Configuration Object:
 * {
 *   key: string,           // Unique identifier for the action
 *   label: string,         // Display text for the action
 *   icon: ReactElement,    // Icon component (optional, defaults based on key)
 *   onClick: function,     // Action handler function(item, action)
 *   show: function|boolean,// Condition to show action (optional, default true)
 *   divider: boolean,      // Show divider after this action (optional)
 *   destructive: boolean,  // Apply error styling (optional)
 *   confirm: {             // Confirmation dialog config (optional)
 *     title: string,
 *     message: string,
 *     confirmLabel: string,  // Default: "Yes"
 *     cancelLabel: string    // Default: "No"
 *   },
 *   eventData: object      // Additional event tracking data (optional)
 * }
 */
const DataGridActions = ({ item, actions = [], onAction, events, menuId }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const generatedMenuId =
    menuId || `datagrid-actions-menu-${item?.id || "default"}`;
  
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();

  // Default icons mapping
  const defaultIcons = {
    edit: <EditIcon fontSize="small" />,
    delete: <DeleteIcon fontSize="small" color="error" />,
    duplicate: <FileCopyIcon fontSize="small" />,
    copy: <FileCopyIcon fontSize="small" />,
    view: <ViewIcon fontSize="small" />,
    download: <DownloadIcon fontSize="small" />,
    share: <ShareIcon fontSize="small" />,
    settings: <SettingsIcon fontSize="small" />,
  };

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const handleActionClick = (action, event) => {
    event.stopPropagation();
    handleClose();

    const executeAction = () => {
      // Call the action's onClick handler
      if (action.onClick) {
        action.onClick(item, action);
      }

      // Call global action handler if provided
      if (onAction) {
        onAction(action.key, item, action);
      }

      // Track event if events object is provided
      if (events && events.logButtonClick && action.eventData) {
        events.logButtonClick(action.eventData);
      }
    };

    // Handle confirmation dialog if configured
    if (action.confirm) {
      showConfirmDialog({
        title: action.confirm.title,
        message: action.confirm.message,
        confirmText: action.confirm.confirmLabel || "Yes",
        cancelText: action.confirm.cancelLabel || "No",
        onConfirm: executeAction,
        onCancel: () => {
          // Track cancel event if configured
          if (
            events &&
            events.logButtonClick &&
            action.confirm.cancelEventData
          ) {
            events.logButtonClick(action.confirm.cancelEventData);
          }
        },
      });
    } else {
      executeAction();
    }
  };

  // Filter actions based on show condition
  const visibleActions = actions.filter((action) => {
    if (typeof action.show === "function") {
      return action.show(item);
    }
    return action.show !== false;
  });

  // Don't render if no visible actions
  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        aria-label="More actions"
        aria-controls={open ? generatedMenuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        id={generatedMenuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "datagrid-actions-button",
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {visibleActions.reduce((menuItems, action, index) => {
          menuItems.push(
            <MenuItem
              key={action.key}
              onClick={(e) => action.disabled ? undefined : handleActionClick(action, e)}
              disabled={action.disabled}
              sx={action.destructive ? { color: "error.main" } : undefined}
            >
              <ListItemIcon>
                {action.icon || defaultIcons[action.key] || (
                  <SettingsIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>{action.label}</ListItemText>
            </MenuItem>,
          );

          if (action.divider && index < visibleActions.length - 1) {
            menuItems.push(<Divider key={`divider-${action.key}`} />);
          }

          return menuItems;
        }, [])}
      </Menu>
      
      <ConfirmDialog />
    </>
  );
};

export default DataGridActions;
